import { createStyles, makeStyles } from '@material-ui/core';
import { createClient } from 'contentful';
import React, { useContext, useEffect, useState } from 'react';
import { Marker } from 'react-map-gl';
import config from '../../../../config';
import { LanguageContext } from '../../../../providers/LanguageProvider';
import mainColors from '../../../../themes/main-colors';
import {
  OutageMarkerView,
  useOutageLeftPanelState,
} from '../../providers/OutageLeftPanelStateProvider';
import {
  ResponsivePageView,
  useOutageResponsivePageTracker,
} from '../../providers/OutageResponsivePageTracker';
import CrcIcon from '../icons/CRCIcon';

const CONTENT_TYPE = 'location';
const CRC_LOCATION_TYPE = 'Community Resource Center';

const client = createClient({
  host: config.contentful.host,
  space: config.contentful.spaceId,
  environment: config.contentful.environmentName,
  accessToken: config.contentful.accessToken,
});

type LocationEntry = {
  fields: {
    entryName: string;
    latitude: number;
    longitude: number;
    description: string;
    address: string;
    locationType?: string;
  };
};

export type CrcMarker = {
  latitude: number;
  longitude: number;
  address: string;
  description: string;
};

async function getCrcMarkersList(locale: string): Promise<CrcMarker[]> {
  try {
    const response = await client.getEntries({
      content_type: CONTENT_TYPE,
      locale: locale,
    });
    return (response.items as LocationEntry[])
      .map(item => {
        if (item.fields.locationType === CRC_LOCATION_TYPE) {
          return {
            latitude: item.fields.latitude,
            longitude: item.fields.longitude,
            address: item.fields.address,
            description: item.fields.description,
          };
        }
        return undefined;
      })
      .filter((marker): marker is CrcMarker => marker !== undefined);
  } catch (error) {
    console.error('Error fetching entries:', error);
    return [];
  }
}

const useStyles = makeStyles(() =>
  createStyles({
    icon: {
      width: '32px',
      height: '32px',
      cursor: 'pointer',
    },
    popup: {
      maxWidth: '200px',
      zIndex: 9999,
    },
  }),
);

export function CommunityResourceCenter() {
  const classes = useStyles();
  const { language } = useContext(LanguageContext);
  const { setPage } = useOutageResponsivePageTracker();
  const [crcMarkersList, setCrcMarkersList] = useState<CrcMarker[]>([]);
  const {
    setOutageMarkerView,

    setCrcMarkerDetails,
  } = useOutageLeftPanelState();

  const handleCrcMarkerClick = (marker: CrcMarker) => {
    setCrcMarkerDetails(marker);
    setOutageMarkerView(OutageMarkerView.CRC);
    setPage(ResponsivePageView.Details);
  };

  useEffect(() => {
    const fetchMarkers = async () => {
      const markers = await getCrcMarkersList(language);
      setCrcMarkersList(markers);
    };

    void fetchMarkers();
  }, [language]);

  return (
    <>
      {crcMarkersList?.map(marker => (
        <Marker
          key={marker.latitude + marker.longitude}
          longitude={marker.longitude}
          latitude={marker.latitude}
          anchor="center"
          style={{ zIndex: 5 }}
        >
          <div
            onClick={e => {
              e.preventDefault();
              handleCrcMarkerClick(marker);
            }}
          >
            <CrcIcon className={classes.icon} color={mainColors.roseRed} />
          </div>
        </Marker>
      ))}
    </>
  );
}
